/**
 * Activity — the audit trail, read back.
 *
 * THERE IS NO WRITE PATH TO THIS DATA, here or anywhere. Rows are added by
 * audit.record() inside the transaction that made the change, so the trail
 * cannot describe something that rolled back — and an API that could append
 * to it would make it evidence of nothing.
 *
 * The action and club filters are built from what the log ACTUALLY contains,
 * not a hardcoded list. An action that stops being recorded disappears from
 * the dropdown, which is itself worth noticing.
 */
import { Fragment, useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import './admin.css'

type Entry = {
  id: number
  when: string | null
  actor: string | null
  on_behalf_of: string | null
  organization: string | null
  organization_code: string | null
  action: string
  entity: string | null
  reason: string | null
  detail: unknown
  ip: string | null
}
type Payload = {
  total: number; limit: number; offset: number
  entries: Entry[]
  actions: string[]
  organizations: { id: number; code: string; name: string }[]
}

/* Actions worth colouring. Everything else is neutral — colouring all of them
   is the same as colouring none. */
function tone(action: string): string {
  if (action.startsWith('impersonation')) return 'adm-chip--impersonate'
  if (action.startsWith('access.refused')) return 'adm-chip--admin'
  if (action.startsWith('membership') || action.startsWith('user.')) return 'adm-chip--staff'
  return ''
}

const when = (iso: string | null) =>
  iso ? iso.replace('T', ' ').slice(0, 19) : '—'

export default function Activity() {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [actor, setActor] = useState('')
  const [action, setAction] = useState('')
  const [orgId, setOrgId] = useState('')
  const [offset, setOffset] = useState(0)
  const [expanded, setExpanded] = useState<number | null>(null)

  const load = useCallback(() => {
    const q = new URLSearchParams({ limit: '100', offset: String(offset) })
    if (actor.trim()) q.set('actor', actor.trim())
    if (action) q.set('action', action)
    if (orgId) q.set('organization_id', orgId)
    api<Payload>(`/api/v1/activity?${q}`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [actor, action, orgId, offset])

  // Debounced so typing an address is one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250)
    return () => clearTimeout(t)
  }, [load])

  // Any filter change starts at the first page; keeping the offset would show
  // page 4 of a result set with two pages and read as "no results".
  useEffect(() => { setOffset(0) }, [actor, action, orgId])

  if (error) {
    return (<><h1 className="page-title">Activity</h1><p className="error">{error}</p></>)
  }
  if (!data) return <p className="muted">Loading…</p>

  const to = Math.min(data.offset + data.limit, data.total)

  return (
    <>
      <h1 className="page-title">Activity</h1>
      <p className="page-sub">
        Every privileged action, written in the same transaction as the change
        it describes. {data.total.toLocaleString('en-US')} entries.
      </p>

      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Person</span>
          <input className="adm-input" placeholder="email contains…"
                 value={actor} onChange={(e) => setActor(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">Action</span>
          <select className="adm-select" value={action}
                  onChange={(e) => setAction(e.target.value)}>
            <option value="">Any action</option>
            {data.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="adm-field">
          <span className="t-label">Club</span>
          <select className="adm-select" value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}>
            <option value="">Any club</option>
            {data.organizations.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
        <span className="adm-bar__spacer" />
        <span className="t-data" style={{ color: 'var(--color-text-faint)' }}>
          {data.total === 0 ? '0' : `${data.offset + 1}–${to}`} of {data.total}
        </span>
      </div>

      <table className="adm-table">
        <thead>
          <tr>
            <th>When (UTC)</th><th>Who</th><th>Action</th>
            <th>Club</th><th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map((e) => (
            <Fragment key={e.id}>
              <tr className="is-clickable" tabIndex={0} role="button"
                  aria-expanded={expanded === e.id}
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault()
                      setExpanded(expanded === e.id ? null : e.id)
                    }
                  }}>
                <td className="adm-when">{when(e.when)}</td>
                <td>
                  <span className="adm-primary">{e.actor ?? 'anonymous'}</span>
                  {e.on_behalf_of && (
                    <span className="adm-chip adm-chip--impersonate"
                          style={{ marginLeft: 6 }}>as {e.on_behalf_of}</span>
                  )}
                </td>
                <td><span className={`adm-chip ${tone(e.action)}`}>{e.action}</span></td>
                <td>{e.organization ?? <span className="muted">—</span>}</td>
                <td>{e.reason ?? <span className="muted">—</span>}</td>
              </tr>
              {expanded === e.id && (
                <tr className="adm-editor">
                  <td colSpan={5}>
                    <div className="adm-editor__inner">
                      <p className="adm-editor__hint">
                        {e.entity ? <>Entity <code>{e.entity}</code>. </> : null}
                        {e.ip ? <>From <code>{e.ip}</code>.</> : 'No address recorded.'}
                      </p>
                      <pre style={{
                        margin: 0, font: '400 12px/1.6 var(--font-mono)',
                        color: 'var(--color-text-body)', whiteSpace: 'pre-wrap',
                      }}>{JSON.stringify(e.detail ?? {}, null, 2)}</pre>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {data.entries.length === 0 && (
            <tr><td colSpan={5} className="adm-empty">
              Nothing matches these filters.
            </td></tr>
          )}
        </tbody>
      </table>

      <div className="adm-pager">
        <button className="adm-btn adm-btn--quiet" disabled={data.offset === 0}
                onClick={() => setOffset(Math.max(0, offset - data.limit))}>
          Newer
        </button>
        <button className="adm-btn adm-btn--quiet" disabled={to >= data.total}
                onClick={() => setOffset(offset + data.limit)}>
          Older
        </button>
      </div>
    </>
  )
}
